import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AddCalendarEvent, AttendanceStatus, CalendarEventModel, KendoCalendarEvent, UpdateCalendarEvent } from "../../../data/entities";
import { CalendarEventService } from "../../../services";
import { formatTimeOnly, showErrorMessage, showSuccessMessage, showWarningMessage, toYearMonthDayLocalString, toYearMonthDayString } from "../../../utils";
import { SchedulerDataChangeEvent } from "@progress/kendo-react-scheduler";
import { useSelector } from "react-redux";
import { getUserTenantId } from "../../../app";

interface CachedRange {
  primaryId: string;    // userId for CalendarIndexPage, locationId for CalendarLocationPage
  secondaryId: string;  // locationId for CalendarIndexPage, providerIds for CalendarLocationPage
  startDate: Date;
  endDate: Date;
}

export function useCalendarEvents() {

  const [calendarEvents, setCalendarEvents] = useState< CalendarEventModel[] >([]);
  const [date, setDate] = useState(new Date());
  const [group, setGroup] = useState<any>(null);
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const cachedRangeRef = useRef<CachedRange | null>(null);
  const dateRef = useRef(date);

  const calendarEventsRef = useRef(calendarEvents);
  calendarEventsRef.current = calendarEvents;

  useEffect(() => {
    dateRef.current = date;
  }, [date]);

  const calendarEventService = useMemo(()=>new CalendarEventService(),[]);
  const tenantId = useSelector(getUserTenantId());

  const mapResult = useMemo(() => mapToKendoFormat(calendarEvents), [calendarEvents]);

  const handleInputChange = useCallback((value: any, userInput: "dateSelection" | "calendarEvents") => {
    if (userInput === "dateSelection") setDate(value);
    if (userInput === "calendarEvents") setCalendarEvents(value);
  }, []);

  const handleManualDataChange = async ({ created, updated, deleted }: SchedulerDataChangeEvent) => {
    setIsLoading(true);

    try {
      // Remove deleted events
      if (deleted.length > 0) {
        const deletedIds: number[] = [];
        await Promise.all(
          deleted.map(async (item) => {
            try {
              const result = await calendarEventService.deleteCalendarEventAsync(item.bookingEventId);
              if (result) {
                showSuccessMessage("Event deleted successfully.");
                deletedIds.push(item.bookingEventId);
              }
            } catch (error: any) {
            }
          })
        );
        const eventsAfterDeletion = calendarEvents.filter(
          (event) => !deletedIds.includes(event.bookingEventId)
        );
        setCalendarEvents([...eventsAfterDeletion]);
      }

      // Add newly created events
      if (created.length > 0) {
        const newEvents = await Promise.all(
          created.map(async (item) => {
            try {
              const newEvent: AddCalendarEvent = {
                customerFirstName: item.customerFirstName,
                customerLastName: item.customerLastName,
                email: item.email,
                phoneNumber: item.phoneNumber,
                locationId: item.locationId,
                tenantId: tenantId ?? 0,
                providerId: item.providerId ?? 0,
                serviceId: item.serviceId ?? 0,
                startDate: toYearMonthDayLocalString(item.start),
                startTime: formatTimeOnly(item.start) ?? "",
                endDate: toYearMonthDayLocalString(item.end),
                endTime: formatTimeOnly(item.end) ?? "",
                notes: item.notes
              };
          
              const calendarEvent = await calendarEventService.addCalendarEventAsync(newEvent);
              if(calendarEvent.warning){
                showWarningMessage(`Created with warning: ${calendarEvent.warning}`);
              }else{
                showSuccessMessage("Created successfully");
              }
              return calendarEvent.event;
              
            } catch (error: any) {
              return null;
            }
          })
        );
        
        const validNewEvents = newEvents.filter((e): e is CalendarEventModel => e !== null);
        if (validNewEvents.length > 0) {
          setCalendarEvents([...calendarEvents, ...validNewEvents]);
        }
      }

      // Update existing events
      if (updated.length > 0) {
        const updatedEvents = await Promise.all(
          calendarEvents.map(async (item) => {
            const upd = updated.find((u) => u.id === item.bookingEventId);
            if (upd) {
              try {
                const updatedCalendarEvent: UpdateCalendarEvent = {
                  ...item,
                  customerFirstName: upd.customerFirstName,
                  customerLastName: upd.customerLastName,
                  email: upd.email,
                  phoneNumber: upd.phoneNumber,
                  serviceId: upd.serviceId,
                  locationId: upd.locationId,
                  providerId: upd.providerId,
                  startDate: toYearMonthDayLocalString(upd.start),
                  startTime: formatTimeOnly(upd.start) ?? "",
                  endDate:  toYearMonthDayLocalString(upd.end),
                  endTime: formatTimeOnly(upd.end) ?? "",
                  notes: upd.notes,
                  attendanceStatus: upd.attendanceStatus ?? item.attendanceStatus,
                };
                const result = await calendarEventService.updateCalendarEventAsync(updatedCalendarEvent);
                if(result.warning){
                  showWarningMessage(`Updated with warning: ${result.warning}`)
                }else{
                  showSuccessMessage("Updated successfully.");
                }
                return result.event;

              } catch (error: any) {
                return item; // Return original item if update fails
              }
            }
            return item;
          })
        );

        setCalendarEvents(prevEvents => [
          // Keep all events except those that are updated
          ...prevEvents.filter(e => !updatedEvents.some(u => u.bookingEventId === e.bookingEventId)),
          // Add updated events
          ...updatedEvents
        ]);
      }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "An unexpected error occurred.";
        showErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const setCalendarGrouping = useCallback((
    resourceType: string,
    infoToMap: any[],
    dataValue: string,
    textValue: string,
    disable?: boolean
  ) => {
    if(disable){
      setGroup(null);
      setResources([]);
      return;
    }
    const dynamicResources = [
      {
        name: resourceType,
        data: infoToMap.map((ce) => ({
          text: ce[textValue], //What we want to show as the title for the column
          value: ce[dataValue], //Associated identifier to group it into that
          color: ce.serviceTypeColor || "rgb(87, 47, 47)",
        })),
        field: `${dataValue}`,
        valueField: "value",
        textField: "text",
        colorField: "color",
      },
    ];

    setResources(dynamicResources);

    const dynamicGroup = {
      resources: [resourceType],
      orientation: "horizontal",
    };
    setGroup(dynamicGroup);
  }, []);

  const exportCalendar = async (userId: number) => {
    try {
      return await calendarEventService.exportCalendarAsync(userId);
    }
    catch(error) {
      showErrorMessage(`Error occurred while trying to export calendar.`);
    }
  };

  const refreshCalendarEvents = useCallback(async (
    fetchFunction: keyof CalendarEventService & string, 
    primaryId: string, 
    secondaryId: string,
    forceRefresh: boolean = false
  ) => {
    try {
      if (typeof calendarEventService[fetchFunction] !== "function") {
        throw new Error(`Method "${fetchFunction}" does not exist on CalendarEventService.`);
      }

      const currentDate = dateRef.current;

      const idsChanged = 
        cachedRangeRef.current?.primaryId !== primaryId || 
        cachedRangeRef.current?.secondaryId !== secondaryId;
      
      const dateOutsideCache = !isWithinCachedRange(currentDate, cachedRangeRef.current);
      
      if (!forceRefresh && !idsChanged && !dateOutsideCache) {
        setIsLoading(false);  
        return;
      }

      setIsLoading(true);
      
      const { startDate, endDate } = getBufferedDateRange(currentDate);
      const startDateStr = toYearMonthDayString(startDate);
      const endDateStr = toYearMonthDayString(endDate);
      
      const events = await (calendarEventService[fetchFunction] as Function)(
        primaryId, 
        secondaryId, 
        startDateStr, 
        endDateStr
      );
      
      cachedRangeRef.current = {
        primaryId,
        secondaryId,
        startDate,
        endDate
      };
      
      events ? handleInputChange(events, "calendarEvents") : handleInputChange([], "calendarEvents");
      setIsLoading(false);
      return events;

    } catch (error) {
      handleInputChange([], "calendarEvents");
      setIsLoading(false);
    }
  }, [calendarEventService, handleInputChange]);

  // Check if date navigation requires new fetch
  const checkDateRangeAndRefresh = useCallback((
    fetchFunction: keyof CalendarEventService & string,
    primaryId: string,
    secondaryId: string
  ) => {
    const currentDate = dateRef.current;
    const withinCache = isWithinCachedRange(currentDate, cachedRangeRef.current);
    
    if (!withinCache) {
      refreshCalendarEvents(fetchFunction, primaryId, secondaryId, true);
    } 

  }, [refreshCalendarEvents]);

  // Clear cache (useful when events are added/updated/deleted)
  const invalidateCache = useCallback(() => {
    cachedRangeRef.current = null;
  }, []);

  const updateAttendanceStatus = useCallback(async (bookingEventId: number, attendanceStatus: AttendanceStatus) => {
    try {
      await calendarEventService.updateAttendanceStatusAsync(bookingEventId, attendanceStatus);
      setCalendarEvents(prevEvents => prevEvents.map(event =>
        event.bookingEventId === bookingEventId
          ? { ...event, attendanceStatus }
          : event
      ));
      const statusLabel = attendanceStatus === AttendanceStatus.CheckedIn
        ? "Marked as checked in."
        : attendanceStatus === AttendanceStatus.NoShow
          ? "Marked as no-show."
          : "Attendance status cleared.";
      showSuccessMessage(statusLabel);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
                        error?.message ||
                        "Failed to update attendance status.";
      showErrorMessage(errorMessage);
    }
  }, [calendarEventService]);

  const findCalendarEvent = useCallback((bookingEventId: number) =>
    calendarEventsRef.current.find(e => e.bookingEventId === bookingEventId), []);

  const moveCalendarEvent = useCallback(async (
    source: CalendarEventModel,
    target: { start: Date; end: Date; providerId?: number }
  ) => {
    setIsLoading(true);
    try {
      const payload: UpdateCalendarEvent = {
        ...source,
        providerId: target.providerId ?? source.providerId,
        startDate: toYearMonthDayLocalString(target.start),
        startTime: formatTimeOnly(target.start) ?? "",
        endDate: toYearMonthDayLocalString(target.end),
        endTime: formatTimeOnly(target.end) ?? "",
      };

      const result = await calendarEventService.updateCalendarEventAsync(payload);
      if (result.warning) {
        showWarningMessage(`Moved with warning: ${result.warning}`);
      } else {
        showSuccessMessage("Appointment moved.");
      }

      setCalendarEvents(prev => {
        const without = prev.filter(e => e.bookingEventId !== source.bookingEventId);
        return result.event ? [...without, result.event] : without;
      });
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message ||
                        error?.message ||
                        "Failed to move appointment.";
      showErrorMessage(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [calendarEventService]);

  return {
    date,
    mapResult,
    resources,
    group,
    isLoading,
    setIsLoading,
    setCalendarGrouping,
    handleInputChange,
    handleManualDataChange,
    exportCalendar,
    refreshCalendarEvents,
    checkDateRangeAndRefresh,
    invalidateCache,
    updateAttendanceStatus,
    findCalendarEvent,
    moveCalendarEvent
  };
}

// Get a buffered date range (2 months before and after current date)
const getBufferedDateRange = (currentDate: Date) => {
  const start = new Date(currentDate);
  const end = new Date(currentDate);
  
  start.setMonth(start.getMonth() - 2);
  start.setDate(1);
  
  end.setMonth(end.getMonth() + 2);
  end.setMonth(end.getMonth() + 1, 0); // Last day of that month
  
  return { startDate: start, endDate: end };
};

const isWithinCachedRange = (date: Date, cachedRange: CachedRange | null): boolean => {
  if (!cachedRange) return false;
  return date >= cachedRange.startDate && date <= cachedRange.endDate;
};

const mapToKendoFormat = (
  calendarEvent: CalendarEventModel[] | null
): KendoCalendarEvent[] => {
  if (!calendarEvent || calendarEvent.length === 0) return [];
  
  return calendarEvent
    .filter((event) => !event.isCancelled)
    .map((event) => ({
    key: event.bookingEventId,
    id: event.bookingEventId,
    start: new Date(`${event.date}T${event.startTime}`),
    end: new Date(`${event.date}T${event.endTime}`),
    isAllDay: false,
    title: event.serviceName,
    description: event.serviceTypeName,
    locationId: event.locationId,
    locationName: event.locationName,
    providerId: event.providerId,
    serviceId: event.serviceId,
    startTime: event.startTime,
    endTime: event.endTime,
    serviceTypeColor: event.serviceTypeColor,
    providerName: event.providerName,
    customerName: `${event.customerFirstName} ${event.customerLastName}`,
    bookingEventId: event.bookingEventId,
    customerFirstName: event.customerFirstName,
    customerLastName: event.customerLastName,
    email: event.email,
    phoneNumber: event.phoneNumber,
    notes: event.notes,
    isCancelled: event.isCancelled,
    attendanceStatus: event.attendanceStatus ?? AttendanceStatus.Pending,
  }));
};